import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authApi, invitationsApi } from '@/lib/api';
import { homePathForRole } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import type { InvitePreview, Role } from '@/lib/types';

const ROLE_LABELS: Record<Role, string> = {
    estudiante: 'estudiante',
    profesor: 'profesor/a',
    maestro_guia: 'maestro/a guía',
    administrador: 'administrador/a',
    super_admin: 'super_admin',
};

export default function InviteAcceptPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get('token') || '';

    const [preview, setPreview] = useState<InvitePreview | null>(null);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!token || token.length !== 64) {
            setPreview({ valid: false, reason: 'not_found' });
            setLoading(false);
            return;
        }
        invitationsApi.preview(token).then(setPreview).finally(() => setLoading(false));
    }, [token]);

    async function handleSubmit(e: FormEvent) {
        e.preventDefault();
        if (!preview?.valid || !preview.role) return;
        setSubmitting(true);
        setError(null);
        try {
            const res = await authApi.registerWithInvite({
                token,
                name: name.trim(),
                password,
            });
            navigate(homePathForRole(res.user.role), { replace: true });
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8">
                <div className="text-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">ConexEd</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Aceptar invitación</p>
                </div>

                {loading && (
                    <div className="text-center py-8">
                        <div className="inline-block w-8 h-8 border-4 border-brand-blue/20 border-t-brand-blue rounded-full animate-spin" />
                        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">Validando invitación...</p>
                    </div>
                )}

                {!loading && !preview?.valid && (
                    <div className="text-center py-6 space-y-4">
                        <div className="text-red-600 dark:text-red-400 font-semibold">Invitación inválida</div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            {{
                                not_found: 'El enlace de invitación no es válido.',
                                revoked: 'Esta invitación fue revocada.',
                                already_accepted: 'Esta invitación ya fue utilizada.',
                                expired: 'La invitación expiró. Pedí una nueva.',
                            }[preview?.reason || 'not_found']}
                        </p>
                        <Link to="/login" className="btn-primary inline-flex">Ir al login</Link>
                    </div>
                )}

                {!loading && preview?.valid && (
                    <>
                        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                            <p className="text-sm text-gray-700 dark:text-gray-300">Has sido invitado/a a:</p>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">{preview.liceo_nombre}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                como <span className="font-semibold">{ROLE_LABELS[preview.role!]}</span>
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Email: {preview.email}</p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <Input
                                label="Tu nombre completo"
                                required
                                minLength={2}
                                maxLength={200}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                            <Input
                                label="Contraseña"
                                type="password"
                                required
                                minLength={6}
                                maxLength={128}
                                hint="Mínimo 6 caracteres"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />

                            {error && (
                                <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
                            )}

                            <Button type="submit" loading={submitting} className="w-full">
                                Crear mi cuenta
                            </Button>
                        </form>

                        <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-4">
                            ¿Ya tienes cuenta? <Link to="/login" className="text-brand-blue hover:underline">Inicia sesión</Link>
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
